import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause, faPlay, faXmark } from "@fortawesome/free-solid-svg-icons";

interface GlobalAudioBarProps {
  isDarkMode?: boolean;
}

interface OpenAudioDetail {
  audioElement: HTMLAudioElement;
  title?: string;
}

export const GlobalAudioBar: React.FC<GlobalAudioBarProps> = ({ isDarkMode }) => {
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [title, setTitle] = useState<string>("");
  const [visible, setVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const hideTimerRef = React.useRef<number | null>(null);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenAudioDetail>).detail;
      if (!detail?.audioElement) return;

      // Clean up previous
      if (audioEl) {
        try {
          audioEl.pause();
        } catch {}
      }

      const el = detail.audioElement;
      setIsLoading(false);
      setAudioEl(el);
      setTitle(detail.title || "");
      setDuration(el.duration || 0);
      setCurrentTime(0);
      setVisible(true);
      // Reset fade if opening new audio
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      setFadeOut(false);

      // Attach listeners
      const onTime = () => setCurrentTime(el.currentTime);
      const onMeta = () => setDuration(el.duration || 0);
      const onEnded = () => {
        setIsPlaying(false);
        // Start 3s fade-out then hide
        setFadeOut(true);
        if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = window.setTimeout(() => {
          setVisible(false);
          setFadeOut(false);
        }, 3000) as unknown as number;
      };

      el.addEventListener("timeupdate", onTime);
      el.addEventListener("loadedmetadata", onMeta);
      el.addEventListener("ended", onEnded);

      // Autoplay if already playing state from source
      try {
        el.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } catch {}

      return () => {
        el.removeEventListener("timeupdate", onTime);
        el.removeEventListener("loadedmetadata", onMeta);
        el.removeEventListener("ended", onEnded);
      };
    };

    const onClose = () => {
      if (audioEl) {
        try {
          audioEl.pause();
          audioEl.src = "";
        } catch {}
      }
      setAudioEl(null);
      setIsPlaying(false);
      setDuration(0);
      setCurrentTime(0);
      setVisible(false);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      setFadeOut(false);
    };

    window.addEventListener("openGlobalAudioBar", onOpen as EventListener);
    window.addEventListener("closeGlobalAudioBar", onClose);
    return () => {
      window.removeEventListener("openGlobalAudioBar", onOpen as EventListener);
      window.removeEventListener("closeGlobalAudioBar", onClose);
    };
  }, [audioEl]);

  if (!visible) return null;

  return (
    <div className={`fixed bottom-2 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-3xl transition-opacity duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
      <div
        className={cn(
          "rounded-full p-2 shadow-2xl backdrop-blur-sm",
          isDarkMode ? "bg-[#12121260]" : "bg-gray-200/90"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-3 rounded-full px-3 py-2 w-full",
            isDarkMode ? "bg-[#3a3a3a] text-white" : "bg-white text-white"
          )}
        >
          {/* Play / Pause */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-10 w-10 rounded-full flex items-center justify-center bg-transparent hover:backdrop-blur-sm",
              isDarkMode ? "hover:bg-white/15" : "hover:bg-black/10"
            )}
            onClick={() => {
              if (!audioEl) return;
              if (isPlaying) {
                audioEl.pause();
                setIsPlaying(false);
              } else {
                audioEl
                  .play()
                  .then(() => {
                    setIsPlaying(true);
                    // Cancel fade if user resumes during linger
                    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
                    setFadeOut(false);
                  })
                  .catch(() => setIsPlaying(false));
              }
            }}
            disabled={isLoading || !audioEl}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isLoading ? (
              <Loader2
                className={cn(
                  "h-5 w-5 animate-spin",
                  isDarkMode ? "text-white" : "text-black"
                )}
              />
            ) : isPlaying ? (
              <FontAwesomeIcon
                icon={faPause}
                className={cn(
                  "h-5 w-5 transition-colors",
                  isDarkMode ? "text-white hover:text-white/70" : "text-black hover:text-black/70"
                )}
              />
            ) : (
              <FontAwesomeIcon
                icon={faPlay}
                className={cn(
                  "h-5 w-5 transition-colors",
                  isDarkMode ? "text-white hover:text-white/70" : "text-black hover:text-black/70"
                )}
              />
            )}
          </Button>

          {/* Title (optional) */}
          {title ? (
            <div
              className={cn(
                "hidden sm:block max-w-[40%] truncate text-sm",
                isDarkMode ? "text-white/90" : "text-black/80"
              )}
            >
              {title}
            </div>
          ) : null}

          {/* Seek / Progress */}
          <div className="flex-1 min-w-0">
            <div
              className={cn(
                "relative h-1.5 rounded-full overflow-hidden",
                isDarkMode ? "bg-white/20" : "bg-black/15"
              )}
            >
              <div
                className={cn(
                  "absolute left-0 top-0 h-full rounded-full",
                  isDarkMode ? "bg-white" : "bg-black"
                )}
                style={{
                  width: `${(currentTime / (duration || 1)) * 100}%`,
                  transition: "width 0.1s linear",
                }}
              />
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={currentTime}
                onChange={(e) => {
                  if (audioEl) {
                    audioEl.currentTime = Number(e.target.value);
                    setCurrentTime(Number(e.target.value));
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isLoading || !audioEl}
                aria-label="Seek"
              />
            </div>
            <div
              className={cn(
                "flex justify-between mt-1 text-[11px] leading-none",
                isDarkMode ? "text-white/70" : "text-black/70"
              )}
            >
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-10 w-10 rounded-full flex items-center justify-center bg-transparent hover:backdrop-blur-sm",
              isDarkMode ? "hover:bg-white/15" : "hover:bg-black/10"
            )}
            onClick={() => {
              window.dispatchEvent(new Event("closeGlobalAudioBar"));
            }}
            disabled={isLoading || !audioEl}
            aria-label="Close Player"
          >
            <FontAwesomeIcon
              icon={faXmark}
              className={cn(
                "h-5 w-5 transition-colors",
                isDarkMode ? "text-white hover:text-white/70" : "text-black hover:text-black/70"
              )}
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GlobalAudioBar;
